import { ActionIcon, Anchor, Button, FileInput, Group, Image, Modal, Select, Stack, Tabs, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconPencil, IconPlus, IconTrash, IconUpload } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createBanner, deleteBanner, getListBanner, updateBanner } from 'src/apis/bannerAPI'
import { uploadProgress } from 'src/apis/uploadApi'
import CustomButton from 'src/components/CustomComponents/CustomButton'
import TableComponent from 'src/components/TableComponent'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { formatDateTimeMinus, size } from 'src/variable/constant'
import {
	BANNER_KIND_OPTIONS,
	BANNER_KIND_TEXT,
	BANNER_LINK_MAX_LENGTH,
	BannerKind,
	END_TIME_QUICK_PICKS,
	PROMOTION_LINK_HELP,
	PROMOTION_LINK_LABEL,
	PROMOTION_SHOWS_COLUMN,
	PROMOTION_SHOWS_LABEL,
	PROMOTION_SHOWS_MIN,
	bannerKindOf,
	bannerLinkError,
	defaultBannerSchedule,
	endTimeAfter,
	overlapsSameKind,
	promotionShowsChoices,
	promotionShowsOf,
} from './bannerSchedule'
import BannerSchedulePicker from './BannerSchedulePicker'

type BannerForm = {
	image_file: File | null
	image_url: string
	link_url: string
	shows_per_user: number
	start_at: Date | null
	end_at: Date | null
}

const getStatus = (banner: any) => {
	const now = dayjs()
	if (now.isBefore(dayjs(banner.start_at))) return 'Scheduled'
	if (now.isBefore(dayjs(banner.end_at))) return 'Active'
	return 'Expired'
}

const waitForImageAvailability = async (imageUrl: string) => {
	const maxAttempts = 10
	for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
		const isAvailable = await new Promise<boolean>((resolve) => {
			const image = new window.Image()
			const cacheBuster = `banner-ready=${Date.now()}-${attempt}`
			image.onload = () => resolve(true)
			image.onerror = () => resolve(false)
			image.src = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}${cacheBuster}`
		})
		if (isAvailable) return
		await new Promise((resolve) => window.setTimeout(resolve, 1000))
	}

	throw new Error('Banner image is still processing. Please try again in a moment.')
}

function BannerPage() {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const [data, setData] = useState<any[]>([])
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(false)
	const [editingBanner, setEditingBanner] = useState<any | null>(null)
	const [modalOpen, setModalOpen] = useState(false)
	const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 1, pageSize: 10 })
	// UD-562: which kind of banner the page lists and creates.
	const [kind, setKind] = useState<BannerKind>('main')
	const latestLoad = useRef(0)
	// The form belongs to the banner being edited, else to the kind on screen.
	const formKind: BannerKind = editingBanner ? bannerKindOf(editingBanner) : kind
	const text = BANNER_KIND_TEXT[kind]
	const formText = BANNER_KIND_TEXT[formKind]

	const form = useForm<BannerForm>({
		initialValues: { image_file: null, image_url: '', link_url: '', shows_per_user: PROMOTION_SHOWS_MIN, start_at: null, end_at: null },
		validate: {
			image_url: (value) => value ? null : 'Banner image is required',
			link_url: (value) => bannerLinkError(value),
			start_at: (value) => value ? null : 'Start time is required',
			end_at: (value, values) => {
				if (!value) return 'End time is required'
				if (values.start_at && !dayjs(value).isAfter(dayjs(values.start_at))) return 'End time must be after start time'
				return null
			},
		},
		validateInputOnBlur: true,
	})

	const loadBanners = useCallback(async () => {
		// A slow answer for the other kind must not land in this kind's list.
		const load = ++latestLoad.current
		setLoading(true)
		try {
			const response = await getListBanner({
				fields: ['$all'], page: pagination.pageIndex, limit: pagination.pageSize,
				order: [['start_at', 'DESC']],
				filter: { type: kind },
			})
			if (load !== latestLoad.current) return
			setData(response?.data?.results?.objects?.rows || [])
			setTotal(response?.data?.results?.objects?.count || 0)
		} catch (error) {
			if (load === latestLoad.current) openError(error)
		} finally {
			if (load === latestLoad.current) setLoading(false)
		}
	}, [kind, openError, pagination])

	useEffect(() => { loadBanners() }, [loadBanners])

	const changeKind = useCallback((value: string | null) => {
		if (value !== 'main' && value !== 'promotion') return
		setKind(value)
		setData([])
		setTotal(0)
		setPagination((previous) => ({ ...previous, pageIndex: 1 }))
	}, [])

	const closeEditor = useCallback(() => {
		setModalOpen(false)
		setEditingBanner(null)
		form.reset()
	}, [form])

	const openCreate = useCallback(() => {
		form.reset()
		form.setValues({ ...defaultBannerSchedule(new Date()), link_url: '', shows_per_user: PROMOTION_SHOWS_MIN })
		setEditingBanner(null)
		setModalOpen(true)
	}, [form])

	const openEdit = useCallback((banner: any) => {
		setEditingBanner(banner)
		form.setValues({
			image_file: null,
			image_url: banner.image_url,
			link_url: banner.link_url || '',
			shows_per_user: promotionShowsOf(banner),
			start_at: new Date(banner.start_at),
			end_at: new Date(banner.end_at),
		})
		setModalOpen(true)
	}, [form])

	const submit = useCallback(async (values: BannerForm) => {
		openLoadingContext()
		try {
			// Only Main Banners may not overlap each other. UD-772: several Banner
			// Promotions may run at the same time (the server gives each account at
			// most one a day, in the CMS order), and a promotion may run during a
			// Main Banner.
			if (formKind === 'main') {
				const scheduleResponse = await getListBanner({
					fields: ['$all'],
					page: 1,
					limit: 3000,
					filter: { type: formKind },
				})
				const hasOverlap = overlapsSameKind(
					scheduleResponse?.data?.results?.objects?.rows || [],
					formKind,
					values.start_at,
					values.end_at,
					editingBanner?.id,
				)
				if (hasOverlap) {
					form.setFieldError('start_at', formText.overlapError)
					return
				}
			}
			const imageUrl = values.image_file
				? await uploadProgress(values.image_file, undefined)
				: values.image_url
			if (values.image_file) {
				await waitForImageAvailability(imageUrl)
			}
			const body: Record<string, any> = {
				image_url: imageUrl,
				start_at: dayjs(values.start_at).startOf('minute').toISOString(),
				end_at: dayjs(values.end_at).startOf('minute').toISOString(),
			}
			// The kind is set once, on create; a banner never changes kind.
			if (!editingBanner) body.type = formKind
			if (formKind === 'promotion') {
				body.link_url = values.link_url.trim() || null
				body.shows_per_user = values.shows_per_user
			}
			const response = editingBanner
				? await updateBanner(editingBanner.id, body)
				: await createBanner(body)
			if (response?.data?.code === 200) {
				closeEditor()
				await loadBanners()
				openModal({ type: 'success', label: `${formText.name} has been ${editingBanner ? 'updated' : 'created'} successfully` })
			}
		} catch (error) {
			openError(error)
		} finally {
			closeLoadingContext()
		}
	}, [
		closeEditor,
		closeLoadingContext,
		editingBanner,
		form,
		formKind,
		formText,
		loadBanners,
		openError,
		openLoadingContext,
		openModal,
	])

	const remove = useCallback((banner: any) => {
		const removedText = BANNER_KIND_TEXT[bannerKindOf(banner)]
		openModal({
			type: 'confirm', label: removedText.deleteQuestion,
			handleConfirm: async () => {
				openLoadingContext()
				try {
					const response = await deleteBanner(banner.id)
					if (response?.data?.code === 200) {
						await loadBanners()
						openModal({ type: 'success', label: `${removedText.name} has been deleted successfully` })
					}
				} catch (error) {
					openError(error)
				} finally {
					closeLoadingContext()
				}
			},
		})
	}, [closeLoadingContext, loadBanners, openError, openLoadingContext, openModal])

	const columns = useMemo((): MRT_ColumnDef[] => [
		{ header: 'Image', accessorKey: 'image_url', size: size.text, Cell: ({ row }: any) => <Image src={row.original.image_url} alt="Banner" width={kind === 'promotion' ? 56 : 120} height={56} fit="cover" radius="sm" /> },
		{ header: 'Start at', accessorKey: 'start_at', size: size.text, Cell: ({ cell }: any) => dayjs(cell.getValue()).format(formatDateTimeMinus) },
		{ header: 'End at', accessorKey: 'end_at', size: size.text, Cell: ({ cell }: any) => dayjs(cell.getValue()).format(formatDateTimeMinus) },
		{ header: 'Status', accessorKey: 'status', size: size.no, Cell: ({ row }: any) => getStatus(row.original) },
		...(kind === 'promotion' ? [{
			header: 'Link', accessorKey: 'link_url', size: size.mediumS,
			Cell: ({ row }: any) => row.original.link_url
				? <Anchor href={row.original.link_url} target="_blank" rel="noopener noreferrer" size="sm" lineClamp={2} sx={{ wordBreak: 'break-all' }}>{row.original.link_url}</Anchor>
				: <Text size="sm" color="dimmed">No link</Text>,
		}, {
			header: PROMOTION_SHOWS_COLUMN, accessorKey: 'shows_per_user', size: size.text,
			Cell: ({ row }: any) => promotionShowsOf(row.original),
		}] : []),
	], [kind])

	return <Stack sx={{ width: '100%' }}>
		<Tabs value={kind} onTabChange={changeKind}>
			<Tabs.List>
				{BANNER_KIND_OPTIONS.map((option) => <Tabs.Tab key={option.value} value={option.value} fw={700}>{option.label}</Tabs.Tab>)}
			</Tabs.List>
		</Tabs>
		<Group position="apart">
			<Text size="sm" color="dimmed">{text.onlyOneNote}</Text>
			<CustomButton variant="outlined" onClick={openCreate}><Group spacing={6}><IconPlus size={18} /><span>{text.createButton}</span></Group></CustomButton>
		</Group>
		<TableComponent
			columns={columns} total={total} data={data} pagination={pagination} loading={loading}
			handleChangePage={(page: number) => setPagination((previous) => ({ ...previous, pageIndex: page }))}
			_renderCustomActions={(row: any) => <Group spacing={8} noWrap>
				<ActionIcon variant="subtle" color="green" aria-label="Edit banner" title="Edit banner" onClick={() => openEdit(row.original)}>
					<IconPencil size={18} />
				</ActionIcon>
				<ActionIcon variant="subtle" color="red" aria-label="Delete banner" title="Delete banner" onClick={() => remove(row.original)}>
					<IconTrash size={18} />
				</ActionIcon>
			</Group>}
		/>
		<Modal
			opened={modalOpen}
			onClose={closeEditor}
			title={editingBanner ? formText.editTitle : formText.createTitle}
			size={900}
			yOffset={16}
			styles={{ content: { minHeight: 'calc(100vh - 32px)' } }}
		>
			<form onSubmit={form.onSubmit(submit)}><Stack>
				<FileInput
					accept="image/png,image/jpeg,image/webp,image/gif"
					label="Banner image"
					description={formText.sizeHint}
					withAsterisk
					icon={<IconUpload size={18} />}
					{...form.getInputProps('image_file')}
					error={form.errors.image_url}
					onChange={(file) => {
						form.setFieldValue('image_file', file)
						if (file) {
							form.setFieldValue('image_url', URL.createObjectURL(file))
							form.clearFieldError('image_url')
						}
					}}
				/>
				{formKind === 'promotion' && <TextInput
					label={PROMOTION_LINK_LABEL}
					description={PROMOTION_LINK_HELP}
					placeholder="https://"
					maxLength={BANNER_LINK_MAX_LENGTH}
					{...form.getInputProps('link_url')}
				/>}
				{formKind === 'promotion' && <Select
					label={PROMOTION_SHOWS_LABEL}
					data={promotionShowsChoices(form.values.shows_per_user)}
					value={String(form.values.shows_per_user)}
					onChange={(value) => { if (value !== null) form.setFieldValue('shows_per_user', Number(value)) }}
					withinPortal
				/>}
				<BannerSchedulePicker
					startAt={form.values.start_at}
					endAt={form.values.end_at}
					startError={form.errors.start_at}
					endError={form.errors.end_at}
					onChange={({ startAt, endAt }) => {
						form.setFieldValue('start_at', startAt)
						form.setFieldValue('end_at', endAt)
						form.validateField('start_at')
						form.validateField('end_at')
					}}
				/>
				<Group spacing={6} position="right">
					<Text size="xs" color="dimmed">End after start:</Text>
					{END_TIME_QUICK_PICKS.map((pick) => <Button key={pick.label} size="xs" compact variant="light" onClick={() => {
						form.setFieldValue('end_at', endTimeAfter(form.values.start_at, pick.hours, new Date()))
						form.clearFieldError('end_at')
					}}>{pick.label}</Button>)}
				</Group>
				{form.values.image_url && <Image src={form.values.image_url} alt="Banner preview" height={160} fit="contain" radius="sm" />}
				<Button variant="outline" type="submit">{editingBanner ? formText.updateButton : formText.createButton}</Button>
			</Stack></form>
		</Modal>
	</Stack>
}

export default React.memo(BannerPage)
